import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { useAuth } from '../../../contexts/AuthContext';

export default function RepairGuidePanel({
  guides = [],
  activeStepIndex,
  onStepSelect,
  onClose,
  currentGuideId,
  onGuideSelect,
  onStartAuthoring,
  onSelectPart,
  selectedComponent,
  showStringLines = false,
  onToggleStringLines,
  onSelectCameraAngle
}) {
  const { isEnterprise, isAdmin } = useAuth();
  const [selectedGuideId, setSelectedGuideId] = useState(currentGuideId || (guides[0]?.id || ''));
  const [forceStepView, setForceStepView] = useState(false);

  useEffect(() => {
    if (selectedComponent) {
      setForceStepView(true);
    } else {
      setForceStepView(false);
    }
  }, [selectedComponent]);

  useEffect(() => {
    if (currentGuideId) {
      setSelectedGuideId(currentGuideId);
    } else if (guides.length > 0 && !selectedGuideId) {
      setSelectedGuideId(guides[0].id);
    }
  }, [currentGuideId]);

  const activeGuide = guides.find(g => g.id === selectedGuideId);
  const steps = activeGuide?.steps || [];
  const currentStep = steps[activeStepIndex];

  const handleGuideChange = (e) => {
    const id = e.target.value;
    setSelectedGuideId(id);
    onGuideSelect?.(id);
  };

  return (
    <div className="flex flex-col h-full bg-slate-950 text-slate-100 p-4 border-r border-slate-800 w-[330px] shadow-2xl overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-slate-800 pb-3 mb-4">
        <div className="flex items-center gap-2">
          <Icon name="Wrench" className="text-green-400 w-5 h-5 animate-pulse" />
          <h3 className="font-bold text-sm tracking-wide uppercase text-white">Repair Sequences</h3>
        </div>
        <button onClick={onClose} className="text-slate-400 hover:text-white transition-colors">
          <Icon name="X" className="w-5 h-5" />
        </button>
      </div>

      {guides.length === 0 ? (
        <div className="flex flex-col items-center justify-center flex-1 text-center py-6 text-slate-400">
          <Icon name="Wrench" className="w-10 h-10 mb-3 text-green-500 animate-pulse" />
          <p className="text-xs leading-relaxed mb-6 font-medium text-slate-300">
            We are actively building our Repair Guides step-by-step diagnostic sequencer! Soon, selecting any 3D component will unlock high-fidelity animations, disassembly guides, torque specs, and tool requirements mapped directly on the 3D model.
          </p>
          {(isEnterprise || isAdmin) ? (
            <Button
              variant="ghost"
              size="sm"
              className="bg-blue-600 hover:bg-blue-500 text-white font-bold text-xs mt-4"
              onClick={() => onStartAuthoring?.(false)}
            >
              Create New Guide
            </Button>
          ) : (
            <Button
              variant="ghost"
              size="sm"
              className="bg-slate-800 hover:bg-slate-700 text-slate-200 font-bold text-xs px-6 py-2 rounded-lg"
              onClick={onClose}
            >
              Close Panel
            </Button>
          )}
        </div>
      ) : !forceStepView && !selectedComponent ? (
        /* View 1: List of Guides when no component is selected */
        <div className="space-y-4 flex flex-col flex-1 overflow-hidden">
          <div className="space-y-1 flex-shrink-0">
            <h4 className="text-[10px] font-bold uppercase text-slate-400">Available Guides</h4>
            <p className="text-[9px] text-slate-500 italic">* Double-click a guide to view its steps.</p>
          </div>

          <div className="flex-1 space-y-2 overflow-y-auto pr-1">
            {guides.map((g) => (
              <div
                key={g.id}
                onDoubleClick={() => {
                  setSelectedGuideId(g.id);
                  onGuideSelect?.(g.id);
                  setForceStepView(true);
                }}
                className="bg-slate-900/60 border border-slate-800/80 rounded-lg p-3 hover:bg-slate-900 hover:border-slate-700 cursor-pointer transition-all select-none group"
              >
                <div className="flex items-center justify-between gap-1 mb-1">
                  <span className="text-xs font-bold text-white group-hover:text-blue-400 transition-colors truncate max-w-[200px]">
                    {g.title}
                  </span>
                  {!g.isPublished && (
                    <span className="text-[8px] bg-yellow-500/10 text-yellow-400 border border-yellow-500/20 px-1 rounded font-semibold">
                      Draft
                    </span>
                  )}
                </div>
                <p className="text-[10px] text-slate-400 line-clamp-2 italic mb-2">
                  {g.description || 'No description provided.'}
                </p>
                <div className="flex items-center justify-between text-[9px] mt-1 text-slate-500">
                  {g.estimatedTime ? (
                    <span className="text-slate-400 flex items-center gap-0.5">⏱️ {g.estimatedTime} mins</span>
                  ) : <span></span>}
                  {g.author && (
                    <span>By: {g.author.firstName} {g.author.lastName}</span>
                  )}
                </div>
              </div>
            ))}
          </div>

          {(isEnterprise || isAdmin) && (
            <div className="border-t border-slate-900 pt-3 mt-auto">
              <Button
                variant="ghost"
                size="sm"
                className="w-full bg-blue-600 hover:bg-blue-500 text-white font-bold text-xs"
                onClick={() => onStartAuthoring?.(false)}
              >
                Create New Guide
              </Button>
            </div>
          )}
        </div>
      ) : (
        /* View 2: Step-by-step Sequencer View */
        <div className="space-y-4 flex flex-col flex-1">
          {/* Guide Selector */}
          <div className="space-y-2">
            <div className="flex items-end justify-between gap-2">
              <div className="flex-1">
                <label className="block text-[10px] font-bold uppercase text-slate-400 mb-1">Select Guide</label>
                <select
                  value={selectedGuideId}
                  onChange={handleGuideChange}
                  className="w-full bg-slate-900 border border-slate-800 rounded px-2 py-1.5 text-xs text-white outline-none focus:border-green-500 transition-colors"
                >
                  {guides.map(g => (
                    <option key={g.id} value={g.id}>
                      {g.title} {g.isPublished ? '' : '(Draft)'}
                    </option>
                  ))}
                </select>
              </div>

              {!selectedComponent && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="bg-slate-900 border border-slate-800 text-slate-400 hover:text-white px-2.5 h-8 text-[10px] font-bold"
                  onClick={() => setForceStepView(false)}
                >
                  ◀ List
                </Button>
              )}
            </div>
            
            {(isEnterprise || isAdmin) && (
              <Button
                variant="ghost"
                size="sm"
                className="w-full bg-blue-600 hover:bg-blue-500 text-white font-bold text-xs"
                onClick={() => onStartAuthoring?.(true)} // Edit current
              >
                Edit Guide
              </Button>
            )}
          </div>

          {activeGuide && (
            <div className="text-[11px] text-slate-400 border-b border-slate-900 pb-3 space-y-1">
              <p className="italic">{activeGuide.description || 'No description provided.'}</p>
              {activeGuide.estimatedTime && (
                <p className="text-[10px] text-slate-300 font-medium flex items-center gap-1 mt-1">
                  ⏱️ Estimated Time: <span className="text-blue-400 font-bold">{activeGuide.estimatedTime} mins</span>
                </p>
              )}
              {activeGuide.author && (
                <p className="text-[9px] text-slate-500">
                  By: {activeGuide.author.firstName} {activeGuide.author.lastName}
                </p>
              )}
            </div>
          )}

          {/* Steps List */}
          <div className="flex-1 space-y-2 overflow-y-auto pr-1">
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-[10px] font-bold uppercase text-slate-400">Sequence Steps</h4>
              <button
                type="button"
                onClick={onToggleStringLines}
                className={`px-2 py-0.5 rounded text-[8px] font-black uppercase transition-all cursor-pointer border ${
                  showStringLines
                    ? 'bg-blue-600/20 text-blue-400 border-blue-500/20'
                    : 'bg-slate-900 border-slate-800 text-slate-400 hover:text-slate-200'
                }`}
                title="Toggles cyan dashed extraction path lines from original position to active step position!"
              >
                String Lines: {showStringLines ? 'ON' : 'OFF'}
              </button>
            </div>
            {steps.map((step, idx) => {
              const isActive = idx === activeStepIndex;
              const isPassed = idx < activeStepIndex;

              return (
                <div
                  key={step.id || idx}
                  onClick={() => onStepSelect(idx)}
                  className={`group relative flex items-start gap-3 p-2.5 rounded-lg border cursor-pointer transition-all ${
                    isActive
                      ? 'bg-green-950/40 border-green-500 text-white shadow-lg'
                      : 'bg-slate-900/60 border-slate-800/80 text-slate-300 hover:bg-slate-900 hover:border-slate-700'
                  }`}
                >
                  {/* Step Status Indicator */}
                  <div className="flex-shrink-0 mt-0.5">
                    {isPassed ? (
                      <div className="w-5 h-5 rounded-full bg-green-500/20 border border-green-500 flex items-center justify-center">
                        <Icon name="Check" className="w-3 h-3 text-green-400" />
                      </div>
                    ) : isActive ? (
                      <div className="w-5 h-5 rounded-full bg-green-500 border border-green-400 flex items-center justify-center animate-pulse">
                        <span className="text-[10px] font-bold text-slate-950">{idx + 1}</span>
                      </div>
                    ) : (
                      <div className="w-5 h-5 rounded-full bg-slate-800 border border-slate-700 flex items-center justify-center group-hover:border-slate-500">
                        <span className="text-[9px] font-bold text-slate-400 group-hover:text-slate-200">{idx + 1}</span>
                      </div>
                    )}
                  </div>

                  {/* Step content */}
                  <div className="flex-1 min-w-0">
                    <p className={`text-xs font-semibold truncate ${isActive ? 'text-white' : 'text-slate-200'}`}>
                      {step.title || `Step ${idx + 1}`}
                    </p>
                    {step.notes && (isActive || isPassed) && (
                      <p className="text-[10px] text-slate-400 mt-1 leading-relaxed line-clamp-3">
                        {step.notes}
                      </p>
                    )}
                    
                    {/* Symmetrical Components Affected selection helper */}
                    {isActive && step.actions && step.actions.length > 0 && (
                      <div className="mt-2.5 space-y-1">
                        <p className="text-[9px] font-black uppercase text-slate-500">Components affected:</p>
                        <div className="flex flex-wrap gap-1">
                          {step.actions.map(act => (
                            <button
                              key={act.partName}
                              onClick={(e) => {
                                e.stopPropagation(); // Avoid selecting/triggering step click
                                onSelectPart?.(act.partName);
                              }}
                              className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-slate-900 border border-slate-800 text-slate-300 hover:text-blue-400 hover:border-blue-500 hover:underline transition-all cursor-pointer truncate max-w-[150px]"
                              title={`Click to select ${act.partName} in 3D scene`}
                            >
                              ⚙️ {act.partName}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Symmetrical Step Viewpoints selector */}
                    {isActive && step.cameraAngles && step.cameraAngles.length > 0 && (
                      <div className="mt-2.5 space-y-1 bg-slate-950/60 p-1.5 rounded border border-slate-900">
                        <p className="text-[9px] font-black uppercase text-slate-500">🎥 Viewpoints:</p>
                        <div className="flex flex-wrap gap-1">
                          {step.cameraAngles.map((ang, aIdx) => (
                            <button
                              key={aIdx}
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation(); // Avoid triggering step select click
                                onSelectCameraAngle?.(ang);
                              }}
                              className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-slate-900 border border-slate-800 text-slate-355 hover:text-blue-400 hover:border-blue-500 transition-all cursor-pointer"
                              title={`Click to glide camera to Viewpoint ${aIdx + 1}`}
                            >
                              View {aIdx + 1}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Navigation Controls */}
          {steps.length > 0 && (
            <div className="flex items-center gap-2 pt-4 border-t border-slate-900 mt-auto">
              <Button
                variant="ghost"
                size="sm"
                className="flex-1 bg-slate-900 border border-slate-800 text-slate-200"
                disabled={activeStepIndex <= 0}
                onClick={() => onStepSelect(activeStepIndex - 1)}
              >
                Previous
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="flex-1 bg-green-600 hover:bg-green-500 text-white font-bold"
                disabled={activeStepIndex >= steps.length - 1}
                onClick={() => onStepSelect(activeStepIndex + 1)}
              >
                Next
              </Button>
            </div>
          )}

          {(isEnterprise || isAdmin) && (
            <div className="border-t border-slate-900 pt-3 mt-2">
              <Button
                variant="ghost"
                size="sm"
                className="w-full bg-slate-900 border border-slate-800 text-slate-200 text-xs"
                onClick={() => onStartAuthoring?.(false)}
              >
                Create New Guide
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
